import { Plus, Search, ShieldCheck } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useAccessRoles } from './api'
import { AssignUsersDialog } from './components/AssignUsersDialog'
import { RoleFormModal } from './components/RoleFormModal'
import { RoleSummaryPanel } from './components/RoleSummaryPanel'
import { RolesTable } from './components/RolesTable'
import { useRoleCommands } from './components/useRoleCommands'
import { ROLE_WRITE_PERM } from './permissions'
import type { RecordStatus, Role } from './types'
import { useAccessLookups } from './useLookups'

export function RolesPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const roles = useAccessRoles()
  const lookups = useAccessLookups()
  const [q, setQ] = useState('')
  const [status, setStatus] = useState<RecordStatus | 'all'>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [sheet, setSheet] = useState<{ open: boolean; role: Role | null }>({ open: false, role: null })
  const [assignFor, setAssignFor] = useState<Role | null>(null)
  const canWrite = can(ROLE_WRITE_PERM)
  const commands = useRoleCommands({
    onDeleted: (r) => r.id === selectedId && setSelectedId(null),
  })

  const filtered = useMemo(() => {
    const term = q.trim().toLocaleLowerCase('tr')
    return (roles.data ?? []).filter(
      (r) => (status === 'all' || r.status === status) && (!term || `${r.name} ${r.description}`.toLocaleLowerCase('tr').includes(term)),
    )
  }, [roles.data, q, status])

  const selected = filtered.find((r) => r.id === selectedId) ?? filtered[0] ?? null
  const total = roles.data?.length ?? 0

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.access')}
        title={t('access.roles.title')}
        description={t('access.roles.subtitle')}
        actions={
          canWrite ? (
            <Button onClick={() => setSheet({ open: true, role: null })}>
              <Plus />
              {t('access.roles.create')}
            </Button>
          ) : null
        }
      />
      <div className="space-y-4">
        <section className="overflow-hidden rounded-2xl border bg-card">
          <div className="flex flex-wrap items-center gap-2 border-b p-3">
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('access.roles.searchPlaceholder')} aria-label={t('access.roles.searchPlaceholder')} className="pl-8" />
            </div>
            <Select
              className="w-40"
              value={status}
              onChange={setStatus}
              aria-label={t('access.roles.columns.status')}
              options={[
                { value: 'all', label: t('access.allStatuses') },
                { value: 'active', label: t('access.status.active') },
                { value: 'passive', label: t('access.status.passive') },
              ]}
            />
            <span className="ml-auto text-xs text-muted-foreground">{t('access.roles.count', { count: filtered.length, total })}</span>
          </div>
          {roles.isError ? (
            <ErrorState error={roles.error} onRetry={() => void roles.refetch()} />
          ) : (
            <>
              <RolesTable
                roles={filtered}
                loading={roles.isLoading}
                selectedId={selected?.id ?? null}
                canWrite={canWrite}
                lookups={lookups}
                commands={commands}
                onSelect={(r) => setSelectedId(r.id)}
                onEdit={(r) => setSheet({ open: true, role: r })}
                onAssign={setAssignFor}
              />
              {!roles.isLoading && filtered.length === 0 ? (
                <EmptyState
                  icon={ShieldCheck}
                  title={total ? t('access.roles.noMatch') : t('access.roles.empty')}
                  description={total ? undefined : t('access.roles.emptyHint')}
                />
              ) : null}
            </>
          )}
        </section>

        {selected ? (
          <RoleSummaryPanel
            role={selected}
            canWrite={canWrite}
            commands={commands}
            onEdit={() => setSheet({ open: true, role: selected })}
            onAssign={() => setAssignFor(selected)}
          />
        ) : null}
      </div>

      <RoleFormModal
        open={sheet.open}
        role={sheet.role}
        onOpenChange={(open) => setSheet((s) => ({ ...s, open }))}
        onSaved={(r) => setSelectedId(r.id)}
      />
      <AssignUsersDialog role={assignFor} onOpenChange={(v) => !v && setAssignFor(null)} />
      {commands.dialog}
    </>
  )
}
